<template>
    <div class="diagram" style="height: 100%" />
</template>

<script>
import { appConfig } from '@/config'
import { EventBus } from '@/utils/eventBus'

import alertIcon from '@/assets/images/alert-circle-outline.svg'
import plusIcon from '@/assets/images/add.svg'
import truckIcon from '@/assets/images/truck.svg'
import arrowRightIcon from '@/assets/images/arrow-right.svg'

// import { go } from 'gojs/release/go-debug-module'
import { go } from 'gojs/release/go-module'

const $ = go.GraphObject.make

export default {
    name: 'DiagramPreview',
    props: {
        model: {
            type: Object,
            default: null
        },
        height: {
            type: Number,
            default: 550
        },
        isJobEditable: {
            type: Boolean
        },
        validations: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            diagram: null,
            diagramActions: {
                canUndo: false,
                canRedo: false
            },
            settings: {
                primaryColor: '#2196f3',
                secondaryColor: '#e04512',
                defaultColor: '#000',
                textColor: '#666',
                disabledColor: '#aaa',
                borderColor: '#f0f0f0',
                strokeColor: '#666',
                selectionColor: '#1e90ff',
                successColor: '#43A047',
                errorColor: '#ff5252',
                fill: '#fff',
                buttonFillOver: '#fef8f6',
                buttonFillPressed: '#f4c0af',
                roundedRectangleParams: {
                    parameter1: 4,
                    spot1: go.Spot.TopLeft,
                    spot2: go.Spot.BottomRight
                }
            }
        }
    },
    mounted() {
        this.diagramInit()
    },
    created() {
        EventBus.$on('updateDiagram', (model) => this.updateModel(model))
        EventBus.$on('diagramZoomToFit', () => this.zoomToFit())
        EventBus.$on('diagramSort', () => this.sort())
        EventBus.$on('diagramUndo', () => this.undo())
        EventBus.$on('diagramRedo', () => this.redo())
        EventBus.$on('diagramReset', () => this.reset())
    },
    destroyed() {
        EventBus.$off()
    },
    methods: {
        diagramInit() {
            const diagram = $(go.Diagram, this.$el, {
                maxSelectionCount: 1,
                'undoManager.isEnabled': false,
                'toolManager.mouseWheelBehavior': go.ToolManager.WheelZoom,
                initialAutoScale: go.Diagram.Uniform,
                maxScale: 1.5,
                minScale: 0.4,
                padding: 50,
                layout: $(go.TreeLayout, {
                    arrangement: go.TreeLayout.ArrangementVertical,
                    angle: 0,
                }),
            })

            diagram.animationManager.initialAnimationStyle = go.AnimationManager.None

            // Block definition
            diagram.nodeTemplate = $(go.Node, 'Auto',
                {
                    deletable: false,
                    isShadowed: true,
                    shadowBlur: 1,
                    shadowOffset: new go.Point(0, 2),
                    shadowColor: 'rgba(0, 0, 0, .25)',
                    selectionAdornmentTemplate:
                        $(go.Adornment, 'Spot',
                            $(go.Panel, 'Auto',
                                $(go.Shape, 'RoundedRectangle', this.settings.roundedRectangleParams,
                                    { fill: null, stroke: this.settings.selectionColor, strokeWidth: 3 }),
                                $(go.Placeholder)
                            ),
                            $('Button', // Add process before
                                { visible: false },
                                {
                                    'ButtonBorder.figure': 'Circle',
                                    'ButtonBorder.strokeWidth': 1,
                                    'ButtonBorder.stroke': this.settings.secondaryColor,
                                    'ButtonBorder.fill': this.settings.fill,
                                    '_buttonStrokeOver': this.settings.secondaryColor,
                                    '_buttonFillOver': this.settings.buttonFillOver,
                                    '_buttonStrokePressed': this.settings.secondaryColor,
                                    '_buttonFillPressed': this.settings.buttonFillPressed,
                                    alignment: new go.Spot(0, 0.5, -26, 0),
                                    click: (event, element) => {
                                        this.$emit('get-available-processes-to-add', {
                                            task: element.part.adornedPart.data,
                                            placement: 'before'
                                        })
                                    },
                                },
                                $(go.Picture,
                                    {
                                        desiredSize: new go.Size(18, 18),
                                        source: plusIcon
                                    },
                                ),
                                new go.Binding('visible', '', item => {
                                    return this.isJobEditable && item.allowPreviousProcess
                                }),
                            ),
                            $('Button', // Add process after
                                { visible: false },
                                {
                                    'ButtonBorder.figure': 'Circle',
                                    'ButtonBorder.strokeWidth': 1,
                                    'ButtonBorder.stroke': this.settings.secondaryColor,
                                    'ButtonBorder.fill': this.settings.fill,
                                    '_buttonStrokeOver': this.settings.secondaryColor,
                                    '_buttonFillOver': this.settings.buttonFillOver,
                                    '_buttonStrokePressed': this.settings.secondaryColor,
                                    '_buttonFillPressed': this.settings.buttonFillPressed,
                                    alignment: new go.Spot(1, 0.5, 21, 0),
                                    click: (event, element) => {
                                        this.$emit('get-available-processes-to-add', {
                                            task: element.part.adornedPart.data,
                                            placement: 'after'
                                        })
                                    }
                                },
                                $(go.Picture,
                                    {
                                        desiredSize: new go.Size(18, 18),
                                        source: plusIcon
                                    },
                                ),
                                new go.Binding('visible', '', item => {
                                    return this.isJobEditable && item.allowNextProcess
                                }),
                            ),
                        ),
                    selectionChanged: this.onSelectionChanged,
                },

                new go.Binding('deletable', '', item => {
                    return item.isDeletable
                }),

                $(go.Shape, 'RoundedRectangle', this.settings.roundedRectangleParams,
                    {
                        stretch: go.GraphObject.Fill,
                        fill: this.settings.fill,
                        stroke: this.settings.borderColor,
                        strokeWidth: 1,
                        name: 'container'
                    }
                ),

                $('PanelExpanderButton', 'componentsList', // Component's expander button
                    {
                        alignment: new go.Spot(1, 0, -8, 8),
                    },
                    new go.Binding('visible', '', item => item.processId !== appConfig.common.processes.cuttingPaperTaskId)
                ),

                $(go.Panel, 'Vertical',
                    $(go.Panel, 'Horizontal',
                        $(go.Panel, 'Vertical', // Interlacing
                            {
                                width: 40
                            },
                            $(go.TextBlock,
                                {
                                    margin: new go.Margin(6, 0, 0, 0),
                                    font: textStyle(14),
                                    stroke: this.settings.defaultColor,
                                },
                                new go.Binding('text', '', (item) => {
                                    return `A: ${item.interlacingA}`
                                }),
                                new go.Binding('stroke', '', (item) => {
                                    return item.interlacingA > 0 ? this.settings.successColor : this.settings.disabledColor
                                }),
                            ),
                            $(go.Picture,
                                {
                                    margin: new go.Margin(-6, 0, 0, 0),
                                    desiredSize: new go.Size(18, 18),
                                    source: arrowRightIcon
                                },
                                new go.Binding('opacity', '', (item) => {
                                    return item.interlacingA > 0 ? 1 : 0.5
                                }),
                            ),
                            $(go.TextBlock,
                                {
                                    margin: new go.Margin(6, 0, 0, 0),
                                    font: textStyle(14),
                                    stroke: this.settings.defaultColor,
                                },
                                new go.Binding('text', '', (item) => {
                                    return `B: ${item.interlacingB}`
                                }),
                                new go.Binding('stroke', '', (item) => {
                                    return item.interlacingB > 0 ? this.settings.successColor : this.settings.disabledColor
                                }),
                            ),
                            $(go.Picture,
                                {
                                    margin: new go.Margin(-6, 0, 0, 0),
                                    desiredSize: new go.Size(18, 18),
                                    source: arrowRightIcon
                                },
                                new go.Binding('opacity', '', (item) => {
                                    return item.interlacingB > 0 ? 1 : 0.5
                                }),
                            ),
                            new go.Binding('visible', '', item => item.processId === appConfig.common.processes.printTaskId)
                        ),
                        $(go.Shape, 'LineV', // Separator
                            {
                                margin: new go.Margin(8, 0, 8, 0),
                                stroke: '#ccc',
                                width: 1,
                                stretch: go.GraphObject.Vertical
                            },
                            new go.Binding('visible', '', item => item.processId === appConfig.common.processes.printTaskId)
                        ),
                        $(go.Panel, 'Vertical',
                            {
                                minSize: new go.Size(290, 0),
                            },
                            $(go.TextBlock, // Imposition ordinal number
                                {
                                    alignment: new go.Spot(0, 0, 9, 9),
                                    font: textStyle(14)
                                },
                                new go.Binding('text', '', item => {
                                    return item.manyImpositions ? '' : item.impositionOrdinalNumber
                                })
                            ),
                            $(go.Panel, 'Horizontal', // First row - process name, icons
                                {
                                    margin: new go.Margin(-14, 28, 0, 28),
                                    height: 30,
                                },
                                $(go.TextBlock, // Process name
                                    {
                                        font: textStyle(14),
                                        stroke: this.settings.defaultColor,
                                        name: 'taskName'
                                    },
                                    new go.Binding('text', '', item => {
                                        return `${item.taskName.toUpperCase()}`
                                    })
                                ),
                                $('Button', // Shipping Order button
                                    {
                                        margin: new go.Margin(-3, 0, 0, 6),
                                        padding: 0,
                                        'ButtonBorder.strokeWidth': 0,
                                        'ButtonBorder.stroke': null,
                                        'ButtonBorder.fill': null,
                                        '_buttonStrokeOver': null,
                                        '_buttonFillOver': null,
                                        '_buttonStrokePressed': null,
                                        '_buttonFillPressed': null,
                                        click: () => {
                                            this.$emit('show-shipping-orders')
                                        },
                                    },
                                    $(go.Picture,
                                        {
                                            desiredSize: new go.Size(18, 18),
                                            source: truckIcon
                                        },
                                    ),
                                    new go.Binding('visible', '', item => {
                                        return item.processId === appConfig.common.processes.packingTaskId || item.processId === appConfig.common.processes.sendingTaskId
                                    }),
                                ),
                                $(go.Picture, // Has description icon
                                    {
                                        margin: new go.Margin(-3, 0, 0, 6),
                                        desiredSize: new go.Size(22, 22),
                                        source: alertIcon
                                    },
                                    new go.Binding('visible', '', item => item.hasDescription)
                                ),
                            ),
                            $(go.Panel, 'Horizontal', // Second row - machine
                                { margin: 0 },
                                $(go.TextBlock,
                                    { margin: new go.Margin(0, 8, 0, 8), font: textStyle(12), stroke: this.settings.secondaryColor, visible: false },
                                    new go.Binding('text', '', item => item.machine),
                                    new go.Binding('visible', '', item => item.machine !== null)
                                ),
                            ),
                            $(go.Panel, 'Horizontal', // Third row - amounts
                                { margin: 6 },
                                $(go.TextBlock,
                                    { margin: 3, font: textStyle(12), stroke: this.settings.textColor },
                                    new go.Binding('text', '', item => {
                                        return `arkuszy brutto: ${item.bruttoAmount}`
                                    })
                                ),
                                $(go.TextBlock,
                                    { margin: 3, font: textStyle(12), stroke: this.settings.textColor },
                                    new go.Binding('text', '', item => {
                                        return `arkuszy netto: ${item.nettoAmount}`
                                    })
                                )
                            )
                        )
                    ),
                    $(go.Shape, 'LineH', // Separator
                        {
                            margin: new go.Margin(0, 8, 0, 8),
                            stroke: '#ccc',
                            height: 1,
                            stretch: go.GraphObject.Horizontal
                        },
                        new go.Binding('visible').ofObject('componentsList')
                    ),
                    $(go.Panel, 'Table', // Component details
                        {
                            name: 'componentsList',
                            visible: false
                        },
                        $(go.Panel, 'TableRow', { isPanelMain: true },
                            $(go.TextBlock,
                                'Nr zam.',
                                { column: 0, margin: 4, font: textStyle(11) },
                            ),
                            $(go.TextBlock,
                                'Komponent',
                                { column: 1, margin: 4, font: textStyle(11) },
                            ),
                            $(go.TextBlock,
                                'Użytki',
                                { column: 2, margin: 4, font: textStyle(11) },
                            ),
                            $(go.TextBlock,
                                'Nakład netto',
                                { column: 3, margin: 5, font: textStyle(11) },
                            ),
                        ),
                        new go.Binding('itemArray', 'impositionTaskOrderComponents'),
                        { margin: 7 },
                        {
                            itemTemplate:
                                $(go.Panel, 'TableRow',
                                    new go.Binding('background', 'back'),
                                    $(go.TextBlock,
                                        new go.Binding('text', '', item => item.orderProductComponent.productionNumber),
                                        { column: 0, margin: 4, font: textStyle(11), stroke: this.settings.textColor },
                                    ),
                                    $(go.TextBlock,
                                        new go.Binding('text', '', item => item.orderProductComponent.name),
                                        { column: 1, width: 80, margin: 4, font: textStyle(11), stroke: this.settings.textColor, wrap: go.TextBlock.WrapFit },
                                    ),
                                    $(go.TextBlock,
                                        new go.Binding('text', 'parts'),
                                        { column: 2, margin: 4, font: textStyle(11), stroke: this.settings.textColor },
                                    ),
                                    $(go.TextBlock,
                                        new go.Binding('text', 'nettoAmount'),
                                        { column: 3, margin: 5, font: textStyle(11), stroke: this.settings.textColor },
                                    ),
                                )
                        },
                    )
                )
            )

            // Disable all default commands
            diagram.commandHandler.doKeyDown = function() {
                const event = diagram.lastInput
                const command = diagram.commandHandler

                if (event.key === 'Del' || event.key === 'Backspace') {
                    if (command.canDeleteSelection()) {
                        command.deleteSelection()
                    }
                }
            }

            diagram.commandHandler.deleteSelection = () => {
                this.$emit('delete-task')
            }

            // Blocks connection settings
            diagram.linkTemplate =
                $(go.Link,
                    {
                        routing: go.Link.AvoidsNodes,
                        corner: 5,
                        relinkableFrom: false,
                        relinkableTo: false,
                        deletable: false,
                        selectable: false
                    },
                    $(go.Shape, {
                        strokeWidth: 2,
                        stroke: this.settings.strokeColor,
                    }),
                    $(go.Shape, {
                        toArrow: 'Standard',
                        fill: this.settings.strokeColor,
                        stroke: this.settings.strokeColor,
                    })
                )

            diagram.addDiagramListener('Modified', () => {
                this.undoManagerCheck()
            })

            diagram.addDiagramListener('ObjectDoubleClicked', event => {
                const part = event.subject.part

                if (!(part instanceof go.Link)) {
                    this.$emit('object-double-clicked', event.subject.part.key)
                }
            })

            this.diagram = diagram

            function textStyle(size) {
                return `${size}px Roboto, sans-serif`
            }
        },
        updateModel(value) {
            const model = new go.GraphLinksModel()

            if (value) {
                for (let item in value) {
                    model[item] = value[item]
                }
            }

            this.diagram.model = model
            this.markNodesAsInvalid()
        },
        markNodesAsInvalid() {
            // Reset all nodes colors to default values
            this.diagram.nodes.each(node => {
                const container = node.findObject('container'),
                    taskName = node.findObject('taskName')

                container.stroke = this.settings.borderColor
                container.strokeWidth = 1
                taskName.stroke = this.settings.defaultColor
            })

            const impositions = Object.values(this.validations.job.jobImposition.$each.$iter)

            impositions.forEach(imposition => {
                const impositionTasks = Object.values(imposition.impositionTask.$each.$iter)
                const invalidTasks = impositionTasks.filter(task => {
                    return task.$invalid ||
                        ((task.$model.processId === appConfig.common.processes.printTaskId || task.$model.processId === appConfig.common.processes.cuttingPaperTaskId) && (imposition.formatA.$invalid || imposition.formatB.$invalid))
                })

                invalidTasks.forEach(task => {
                    const node = this.diagram.findNodeForKey(task.$model.id)

                    if (node !== null) {
                        const container = node.findObject('container'),
                            taskName = node.findObject('taskName')

                        container.stroke = this.settings.errorColor
                        container.strokeWidth = 3
                        taskName.stroke = this.settings.errorColor
                    }
                })
            })
        },
        onSelectionChanged(node) {
            node.isSelected ? this.$emit('selection-changed', node.key) : this.$emit('selection-changed')
        },
        zoomToFit() {
            this.diagram.zoomToFit()
        },
        sort() {
            this.diagram.layoutDiagram(true)
        },
        reset() {
            this.updateModel(this.model)
            this.diagram.zoomToFit()
            this.undoManagerCheck()
        },
        undo() {
            this.diagram.undoManager.undo()
            this.undoManagerCheck()
        },
        redo() {
            this.diagram.undoManager.redo()
            this.undoManagerCheck()
        },
        undoManagerCheck() {
            this.diagramActions.canUndo = this.diagram.undoManager.canUndo()
            this.diagramActions.canRedo = this.diagram.undoManager.canRedo()
            this.$emit('diagram-actions', this.diagramActions)
        },
    }
}
</script>
